import cn from "classnames";
import { useEffect, useState } from "react";
import { Chart as ChartJS, LineElement, PointElement, LinearScale, CategoryScale, TimeScale, Filler, Tooltip } from "chart.js";
import "chartjs-adapter-date-fns";
import { Line } from "react-chartjs-2";
import { format as formatDate } from "date-fns";
import { Device, Errors, ErrorTrendBucket } from "@app/shared";
import { Icons } from "../../shared";
import { HoverTooltip } from "../../shared/HoverTooltip";

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, TimeScale, Filler, Tooltip);

// Line colors, shared between the chart datasets and the little legend below it.
const ALL_COLOR = "rgb(99, 102, 241)"; // indigo — total across all firmware
const LATEST_COLOR = "rgb(217, 119, 6)"; // amber — newest reported firmware

type TrendResponse = Awaited<ReturnType<typeof Errors.getDeviceErrorTrend>>;

// Stale-while-revalidate: repaint the last trend instantly on remount (device page and
// back), then let the background fetch swap in fresh data. The server also caches this
// endpoint briefly; both layers are safe because trend data is hour-bucketed volume.
const trendCache = new Map<string, { result: TrendResponse; at: number }>();
const TREND_CACHE_TTL_MS = 60_000;

/**
 * Aggregated trend sparkline for device errors over the last 5 days (hourly buckets).
 * Filter-independent by design: this is the "is anything spiking right now" signal,
 * shown alongside the Entries / Occurrences / Devices cards.
 *
 * Two lines: total error volume across all firmware, plus the slice attributable to the
 * newest reported firmware version, so a fresh rollout that starts regressing stands out.
 */
export const DeviceErrorTrendCard: React.FC<{ device?: Device }> = ({ device }) => {
    const [buckets, setBuckets] = useState<ErrorTrendBucket[]>([]);
    const [latestFirmware, setLatestFirmware] = useState<string | undefined>();
    const [latestDeviceCount, setLatestDeviceCount] = useState(0);
    const [otherDeviceCount, setOtherDeviceCount] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const cacheKey = device?.id ?? "all";
        const applyResult = (result: TrendResponse) => {
            setBuckets(result?.buckets ?? []);
            setLatestFirmware(result?.latestFirmware ?? undefined);
            setLatestDeviceCount(result?.latestFirmwareDeviceCount ?? 0);
            setOtherDeviceCount(result?.otherFirmwareDeviceCount ?? 0);
        };
        const cached = trendCache.get(cacheKey);
        if (cached && Date.now() - cached.at < TREND_CACHE_TTL_MS) {
            applyResult(cached.result);
        }
        (async () => {
            const result = device
                ? await Errors.getDeviceErrorTrendForDevice(device.id)
                : await Errors.getDeviceErrorTrend();
            trendCache.set(cacheKey, { result, at: Date.now() });
            if (!cancelled) {
                applyResult(result);
            }
        })();
        return () => { cancelled = true; };
    }, [device]);

    const total = buckets.reduce((sum, b) => sum + (b.count ?? 0), 0);
    const showLatest = !!latestFirmware;
    const latestLabel = latestFirmware ? `v${latestFirmware}` : "Latest firmware";

    // Errors-per-device comparison (fleet view only): normalize the window's error volume by
    // the *current* number of devices on each build, so a low-population new build isn't
    // unfairly flattered/penalised by raw volume alone. The denominator is a current snapshot —
    // we don't track which build each device ran per hour — hence "approximate" in the tooltip.
    const latestErrors = buckets.reduce((sum, b) => sum + (b.latestFirmwareCount ?? 0), 0);
    const otherErrors = Math.max(0, total - latestErrors);
    const latestRate = latestDeviceCount > 0 ? latestErrors / latestDeviceCount : undefined;
    const baselineRate = otherDeviceCount > 0 ? otherErrors / otherDeviceCount : undefined;
    const showRate = !device && showLatest && latestRate !== undefined;
    const fmtRate = (r: number) => (r >= 10 ? Math.round(r).toLocaleString() : r.toFixed(1));

    // Cap the y-axis at the 98th percentile of non-zero buckets so a single legacy
    // backfilled mega-bucket (the migration clamped legacy device_errors counts to 200M)
    // can't squash recent real-world activity to a flat line. Points above the cap are
    // clipped at the top edge — visually telling you "off the chart" rather than hiding
    // the rest of the data. The cap is derived from the overall counts; the latest-firmware
    // slice is always <= overall per bucket, so it clips against the same ceiling.
    const rawCounts = buckets.map((b) => b.count ?? 0);
    const rawLatest = buckets.map((b) => b.latestFirmwareCount ?? 0);
    const nonZero = rawCounts.filter((c) => c > 0).sort((a, b) => a - b);
    const p98 = nonZero.length > 0 ? nonZero[Math.floor((nonZero.length - 1) * 0.98)] : 0;
    const yMax = p98 > 0 ? Math.max(p98 * 1.2, 10) : undefined;
    const hasOutliers = yMax !== undefined && rawCounts.some((c) => c > yMax);

    // Plot capped values so the line plateaus at the visual ceiling — points above the cap
    // would render off-canvas and tooltip hit-testing would miss them. Tooltips read the
    // raw count via dataIndex so the user still sees the true number on hover.
    const cap = (counts: number[]) => (yMax !== undefined ? counts.map((c) => Math.min(c, yMax)) : counts);
    const displayCounts = cap(rawCounts);
    const displayLatest = cap(rawLatest);

    const data = {
        labels: buckets.map((b) => b.timeBucket),
        datasets: [
            {
                label: "All firmware",
                data: displayCounts,
                borderColor: ALL_COLOR,
                borderWidth: 2,
                backgroundColor: "rgba(99, 102, 241, 0.15)",
                fill: true,
                pointRadius: 0,
                pointHoverRadius: 3,
                tension: 0,
            },
            ...(showLatest
                ? [{
                    label: latestLabel,
                    data: displayLatest,
                    borderColor: LATEST_COLOR,
                    borderWidth: 1.5,
                    backgroundColor: LATEST_COLOR,
                    fill: false,
                    pointRadius: 0,
                    pointHoverRadius: 3,
                    tension: 0,
                }]
                : []),
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: true,
                intersect: false,
                mode: "index" as const,
                callbacks: {
                    title: (items: { parsed: { x: number } }[]) => {
                        const t = items[0]?.parsed?.x;
                        return typeof t === "number" ? formatDate(new Date(t), "EEE HH:mm") : "";
                    },
                    label: (item: { datasetIndex: number; dataIndex: number; parsed: { y: number }; dataset: { label?: string } }) => {
                        const raw = (item.datasetIndex === 0 ? rawCounts : rawLatest)[item.dataIndex] ?? item.parsed.y;
                        const capped = yMax !== undefined && raw > yMax;
                        const name = item.dataset.label ?? "";
                        return `${name}: ${raw.toLocaleString()} errors${capped ? " (off scale)" : ""}`;
                    },
                },
            },
        },
        scales: {
            x: {
                type: "time" as const,
                // Day-unit ticks give one tick per day boundary across the window. Without
                // this chart.js's autoSkip with maxTicksLimit drops the last day, which is
                // exactly the one you most want to see ("today").
                time: { unit: "day" as const, displayFormats: { day: "EEE" } },
                grid: { display: false },
                border: { display: false },
                ticks: {
                    color: "#9ca3af",
                    font: { size: 9 },
                    maxRotation: 0,
                    autoSkip: false,
                    callback: (val: string | number) => {
                        const d = new Date(val);
                        // Today's day-boundary tick reads as "Today" so the right edge of the
                        // chart always tells you where "now" is at a glance.
                        const today = new Date();
                        if (d.getFullYear() === today.getFullYear()
                            && d.getMonth() === today.getMonth()
                            && d.getDate() === today.getDate()) return "Today";
                        return formatDate(d, "EEE");
                    },
                },
            },
            // No max here — values are pre-capped in `displayCounts` above so the axis
            // auto-scales to the cap, keeping hit-test geometry inside the visible area.
            y: { display: false, beginAtZero: true },
        },
        animation: false as const,
        elements: { line: { borderJoinStyle: "round" as const } },
        layout: { padding: { left: 2, right: 2 } },
    };

    return (
        <div className="border border-separators-page p-5 flex flex-1 gap-3 min-w-[16rem] flex-grow shadow-sm">
            <div className="text-center bg-background-page-highlight-gray rounded-full h-16 w-16 flex flex-col items-center">
                <Icons.Chart className="h-10 w-10 mx-auto my-auto" />
            </div>
            <div className="flex flex-col flex-grow gap-1 min-w-0">
                <div className="flex items-center justify-end gap-1">
                    <p className="uppercase text-lg whitespace-nowrap">Trend (5d)</p>
                    <HoverTooltip hoverable={<span className="cursor-help"><Icons.Info className="text-brand-primary h-3.5 w-3.5" /></span>}>
                        <div className="text-left text-xs text-gray-600 space-y-2 max-w-xs">
                            <p>
                                <span className="font-semibold" style={{ color: ALL_COLOR }}>All firmware</span> — total error
                                occurrences per hour across every firmware version (the unchanged overall trend).
                            </p>
                            <p>
                                <span className="font-semibold" style={{ color: LATEST_COLOR }}>{latestLabel}</span> — the slice
                                of that volume coming from the newest reported firmware build.
                            </p>
                            <p>
                                Watch for the {latestLabel} line growing as a share of the total after a rollout — that's the
                                signal a new build is regressing.
                            </p>
                            {showRate && (
                                <p>
                                    <span className="font-semibold">errors/device</span> normalizes that volume by the number of
                                    devices on each build, so you can compare the newest build against the rest of the fleet
                                    fairly — higher means the new build is noisier per device.
                                </p>
                            )}
                            <p className="text-gray-400">
                                Note: the chart lines are raw error <span className="italic">volume</span>; the newest build runs
                                on fewer devices, so a lower line alone doesn't mean it's more reliable. The errors/device figure
                                uses a current device-count snapshot (we don't track per-hour build membership), so it's
                                approximate.
                            </p>
                        </div>
                    </HoverTooltip>
                </div>
                <div className="flex-grow h-16 relative">
                    {buckets.length > 0 ? (
                        <Line data={data} options={options} />
                    ) : (
                        <p className="text-right text-sm text-gray-500 italic">no occurrences</p>
                    )}
                </div>
                {buckets.length > 0 && (
                    <div className="flex items-center justify-end flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: ALL_COLOR }} />
                            All
                        </span>
                        {showLatest && (
                            <span className="flex items-center gap-1 min-w-0">
                                <span className="inline-block w-2.5 h-[3px] shrink-0" style={{ backgroundColor: LATEST_COLOR }} />
                                <span className="truncate">{latestLabel}</span>
                            </span>
                        )}
                        <span className="whitespace-nowrap">{total.toLocaleString()} total{hasOutliers ? " · p98" : ""}</span>
                    </div>
                )}
                {showRate && (
                    <div className="flex items-center justify-end flex-wrap gap-x-1.5 text-xs text-gray-500">
                        <span>errors/device:</span>
                        <span className="font-semibold whitespace-nowrap" style={{ color: LATEST_COLOR }}>
                            {latestLabel}
                        </span>
                        <span>
                            {fmtRate(latestRate!)}
                        </span>
                        {baselineRate !== undefined && (
                            <>
                                <span
                                    className={cn(
                                        "font-semibold",
                                        latestRate! > baselineRate ? "text-red-600" : "text-green-600"
                                    )}
                                >
                                    {latestRate! > baselineRate ? "▲" : "▼"}
                                </span>
                                <span className="whitespace-nowrap">vs {fmtRate(baselineRate)} fleet</span>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
